import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from '../api/apiClient';
import useDocumentTitle from '../hooks/useDocumentTitle';

function LoginPage({ onLoginSuccess, redirectTo }) {
  useDocumentTitle('Anmelden');
  const [schuetzenId, setSchuetzenId] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await Auth.login(schuetzenId.trim(), password, rememberMe);
      const accessToken = response.access_token || response.token;

      if (!accessToken) {
        throw new Error('Kein access_token in der Antwort enthalten');
      }

      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false');
      const userData = response.user || { schuetzenId, vorname: '', nachname: '' };
      localStorage.setItem('currentUser', JSON.stringify(userData));

      if (onLoginSuccess) {
        onLoginSuccess(userData, response.passwort_aktualisieren === true);
      }

      if (response.passwort_aktualisieren === true) {
        navigate('/passwort-aendern');
      } else if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Anmeldung fehlgeschlagen: ' + err.message);
    }
    setLoading(false);
  };

  return (
    <div className="login-screen min-h-screen flex flex-col items-center justify-center gap-6 px-5 py-8 font-sans">
      <style>{`
        .login-screen {
          background: linear-gradient(160deg, #3a9a4a 0%, #2e7d3a 55%, #164a20 100%);
        }
        @media (prefers-color-scheme: dark) {
          .login-screen {
            background: linear-gradient(160deg, #14431f 0%, #0c2f15 60%, #04160a 100%);
          }
          .login-screen .login-card {
            background-color: #16231a;
            box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, .5);
          }
          .login-screen .login-title { color: #e8f0ea; }
          .login-screen .field-label { color: #a9bdae; }
          .login-screen .login-subtitle { color: #a9bdae; }
          .login-screen .field-input {
            background-color: #0f1a12;
            border-color: rgba(255, 255, 255, .12);
            color: #e8f0ea;
          }
          .login-screen .field-input::placeholder { color: #6f8577; }
          .login-screen .remember-label { color: #a9bdae; }
          .login-screen .login-company { color: rgba(255, 255, 255, .7); }
        }
      `}</style>

      <main className="login-card card w-full max-w-[25rem] rounded-2xl border-0 px-8 py-10 text-center shadow-[0_1.5rem_3rem_rgba(15,40,20,0.28)] sm:px-9">
        {/* Logo / Brand */}
        <img
          src="/rwk-logo.png"
          alt="Logo RWK Manager"
          width={128}
          height={128}
          className="mx-auto mb-2 h-32 w-32 object-contain"
        />

        <h1 className="login-title mb-6 text-xl font-bold tracking-tight text-slate-900">RWK Manager</h1>
        <p className="login-subtitle -mt-5 mb-6 text-sm text-slate-500">Rundenwettkampf-Verwaltung</p>

        {error && (
          <div className="alert-error mb-4 rounded-lg text-left">
            <svg className="h-4 w-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div className="field-group">
            <label className="field-label" htmlFor="schuetzenId">Schützen-ID</label>
            <input
              id="schuetzenId"
              type="text"
              inputMode="numeric"
              className="field-input"
              placeholder="Schützen-ID eingeben"
              value={schuetzenId}
              onChange={(e) => setSchuetzenId(e.target.value)}
              disabled={loading}
              required
              autoFocus
            />
          </div>

          <div className="field-group">
            <label className="field-label" htmlFor="password">Passwort</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="field-input pr-10"
                placeholder="Passwort eingeben"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <label className="remember-label flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loading}
            />
            <span className="text-sm text-slate-600">Angemeldet bleiben</span>
          </label>

          <button
            type="submit"
            className="btn-primary btn w-full mt-2"
            disabled={loading}
          >
            {loading && <span className="spinner" />}
            {loading ? 'Anmelden…' : 'Anmelden'}
          </button>

          {/* Nutzungsbedingungen vorerst deaktiviert
          <p className="m-0 text-center text-xs text-slate-500">
            Mit der Anmeldung akzeptieren Sie die{' '}
            <Link to="/nutzungsbedingungen" className="font-medium text-brand-600 hover:text-brand-700">
              Nutzungsbedingungen
            </Link>.
          </p>
          */}
        </form>
      </main>

      <p className="login-company m-0 text-center text-xs leading-normal text-white/85">
        RWK Gruppe<br />Diözesanverband Paderborn
      </p>
    </div>
  );
}

export default LoginPage;
